import { cva, type VariantProps } from 'class-variance-authority'
import type { ComponentType, SVGProps } from 'react'
import { cn } from '../../utils/cn'
import type { TestIdProps } from '../internal/test-id'

/**
 * Wraps an icon component in the system's colour and size scale.
 *
 * The icon itself is a prop rather than a name, so the package depends on no
 * icon library. Every app runs lucide, and passing the component keeps that
 * choice in the app rather than in the package.
 *
 * Icon tones are their own family rather than reusing text tones, because an
 * icon at 16px needs more contrast than text at the same size to read as
 * clearly.
 */
const iconVariants = cva('shrink-0', {
    variants: {
        tone: {
            /**
             * Takes the colour of whatever encloses it.
             *
             * For a glyph inside a control that already sets a colour per
             * variant and per state. A Button's icon has to follow its intent,
             * its hover and its disabled colour, and a fixed tone here would
             * fight all three.
             */
            inherit: 'text-inherit',
            default: 'text-foreground',
            muted: 'text-foreground-muted',
            disabled: 'text-foreground-disabled',
            onColor: 'text-foreground-brand',
            success: 'text-foreground-success',
            warning: 'text-foreground-warning',
            danger: 'text-foreground-danger',
            info: 'text-foreground-info'
        }
    },
    defaultVariants: { tone: 'default' }
})

/** A numeric scale, kept so migration from the donors stays mechanical. */
const SIZES = { 1: 12, 2: 16, 3: 20, 4: 24, 5: 28, 6: 32, 7: 36, 8: 40 } as const

export type IconProps = TestIdProps &
    VariantProps<typeof iconVariants> & {
        icon: ComponentType<SVGProps<SVGSVGElement> & { size?: number | string; strokeWidth?: number }>
        size?: keyof typeof SIZES
        strokeWidth?: number
        /** Names the icon when it carries meaning on its own. Omit when decorative. */
        label?: string
    }

export const Icon = ({
    icon: Component,
    tone,
    size = 2,
    strokeWidth = 1.2,
    label,
    'data-testid': testId
}: IconProps) => (
    <Component
        data-testid={testId}
        size={SIZES[size]}
        strokeWidth={strokeWidth}
        className={cn(iconVariants({ tone }))}
        role={label ? 'img' : undefined}
        aria-label={label}
        aria-hidden={label ? undefined : true}
        focusable="false"
    />
)
